import { ChevronDown, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  LOCALE_TO_LANGUAGE_LABEL_KEY,
  normalizeLocaleCode,
  SUPPORTED_LOCALES,
  type SupportedLocale
} from '../../i18n/locale';

interface LanguageSwitcherProps {
  className?: string;
  compact?: boolean;
  idPrefix?: string;
}

export default function LanguageSwitcher({
  className = '',
  compact = false,
  idPrefix = 'language-switcher'
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation('common');

  const currentLocale = normalizeLocaleCode(i18n.resolvedLanguage || i18n.language);
  const labelId = `${idPrefix}-label`;
  const selectId = `${idPrefix}-select`;

  const handleChange = (nextLocale: SupportedLocale) => {
    void i18n.changeLanguage(nextLocale);
  };

  const getLocaleLabel = (locale: SupportedLocale) => {
    if (compact) {
      return locale.toUpperCase();
    }

    return t(LOCALE_TO_LANGUAGE_LABEL_KEY[locale]);
  };

  return (
    <div
      className={[
        'relative inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-xl border border-slate-200/70 bg-white/85 px-3 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80',
        compact ? 'min-w-[4.75rem] justify-center px-2.5' : 'min-w-[11rem]',
        className
      ].join(' ').trim()}
    >
      <Languages className="h-4 w-4 text-slate-500 dark:text-slate-300" aria-hidden="true" />
      {!compact && (
        <label id={labelId} htmlFor={selectId} className="text-xs font-medium text-slate-600 dark:text-slate-300">
          {t('labels.language')}
        </label>
      )}

      <div className={compact ? 'relative w-[3.35rem]' : 'relative min-w-[7.5rem] flex-1'}>
        <select
          id={selectId}
          value={currentLocale}
          onChange={(event) => handleChange(event.target.value as SupportedLocale)}
          aria-label={compact ? t('labels.language') : undefined}
          aria-labelledby={compact ? undefined : labelId}
          className={[
            'h-7 w-full appearance-none rounded-md bg-transparent pr-5 text-xs font-semibold text-slate-700 outline-none transition dark:text-slate-100',
            compact ? 'pl-0 text-center uppercase tracking-wide' : 'pl-1 text-left'
          ].join(' ')}
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <option key={locale} value={locale} className="text-slate-900 dark:text-slate-100">
              {getLocaleLabel(locale)}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-0.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
